import { Injectable, OnDestroy } from '@angular/core';
import { HSubComponent } from '../tree.model';

// Height budget for one /expand page: question.component.html passes
// [itemHeight]="340" to the carousel and .item/.card are 340px tall with
// overflow hidden, so anything taller gets clipped at runtime.
export const EXPAND_ITEM_HEIGHT = 340;

// .card / .container width in question.component.scss.
export const EXPAND_CARD_WIDTH = 1180;

export interface ExpandPageHeightInfo {
  height: number;
  budget: number;
  over: boolean;
  overBy: number;
  fillPercent: number;
}

/**
 * Measures the rendered height of an /expand page (a 'page' sub-component)
 * as it would appear inside the carousel item on the /expand route.
 *
 * The page elements are rendered into a hidden off-screen node that
 * replicates the /expand markup: a `.container > .item` ancestry so the
 * global typography in _global.scss applies, plus the component-scoped
 * dimensions from question.component.scss recreated inline (those styles
 * are encapsulated and don't reach the editor).
 */
@Injectable({ providedIn: 'root' })
export class ExpandPageHeightService implements OnDestroy {
  private host: HTMLElement | null = null;
  private item: HTMLElement | null = null;
  private cache = new Map<string, number>();

  measureInfo(page: HSubComponent): ExpandPageHeightInfo {
    const height = this.measure(page);
    return {
      height,
      budget: EXPAND_ITEM_HEIGHT,
      over: height > EXPAND_ITEM_HEIGHT,
      overBy: Math.max(0, height - EXPAND_ITEM_HEIGHT),
      fillPercent: Math.min(100, Math.round((height / EXPAND_ITEM_HEIGHT) * 100))
    };
  }

  measure(page: HSubComponent): number {
    const signature = this.signatureOf(page);
    const cached = this.cache.get(signature);
    if (cached !== undefined) return cached;

    const item = this.ensureMeasureItem();
    item.replaceChildren(...this.renderElements(page));
    const height = item.offsetHeight;
    this.watchPendingImages(item, signature);
    item.replaceChildren();

    if (this.cache.size > 300) this.cache.clear();
    this.cache.set(signature, height);
    return height;
  }

  ngOnDestroy() {
    this.host?.remove();
    this.host = null;
    this.item = null;
    this.cache.clear();
  }

  private signatureOf(page: HSubComponent): string {
    return JSON.stringify((page.elements ?? []).map((el: any) => [
      el?.type ?? '',
      el?.value ?? '',
      (el?.elements ?? []).map((nested: any) => nested?.value ?? '')
    ]));
  }

  private ensureMeasureItem(): HTMLElement {
    if (this.item && this.host?.isConnected) return this.item;

    const host = document.createElement('div');
    host.className = 'container';
    host.setAttribute('aria-hidden', 'true');
    Object.assign(host.style, {
      position: 'fixed',
      left: '-99999px',
      top: '0',
      width: `${EXPAND_CARD_WIDTH}px`,
      maxWidth: `${EXPAND_CARD_WIDTH}px`,
      padding: '0',
      margin: '0',
      visibility: 'hidden',
      pointerEvents: 'none',
      zIndex: '-1'
    });

    // Mirror .item from question.component.scss, but with height:auto so the
    // natural content height can be read and compared against the 340px budget.
    const item = document.createElement('div');
    item.className = 'item';
    Object.assign(item.style, {
      boxSizing: 'border-box',
      padding: '10px 30px 0px 30px',
      textAlign: 'left',
      height: 'auto',
      width: '100%'
    });

    host.appendChild(item);
    document.body.appendChild(host);
    this.host = host;
    this.item = item;
    return item;
  }

  // Mirrors the element rendering in question.component.html.
  private renderElements(page: HSubComponent): Node[] {
    const nodes: Node[] = [];
    for (const element of (page.elements ?? []) as any[]) {
      const type = element?.type?.toString();
      switch (type) {
        case 'header': {
          const h4 = document.createElement('h4');
          h4.className = 'answer-header';
          h4.textContent = element.value ?? '';
          nodes.push(h4);
          break;
        }
        case 'br':
          nodes.push(document.createElement('br'));
          break;
        case 'hr':
          nodes.push(document.createElement('hr'));
          break;
        case 'ul': {
          const ul = document.createElement('ul');
          for (const nested of element.elements ?? []) {
            const li = document.createElement('li');
            li.textContent = nested?.value ?? '';
            ul.appendChild(li);
          }
          nodes.push(ul);
          break;
        }
        case 'p': {
          const p = document.createElement('p');
          p.append(...this.parseHtml(element.value ?? ''));
          nodes.push(p);
          break;
        }
        // Other element types are not rendered on /expand, so they
        // contribute no height.
      }
    }
    return nodes;
  }

  // The runtime renders p values via [innerHTML], which Angular sanitizes.
  // Approximate that here: parse inert, drop active content.
  private parseHtml(value: string): Node[] {
    const template = document.createElement('template');
    template.innerHTML = value;
    template.content.querySelectorAll('script, style, iframe, object, embed, link').forEach(node => node.remove());
    template.content.querySelectorAll('*').forEach(node => {
      for (const attr of Array.from(node.attributes)) {
        if (attr.name.startsWith('on') || attr.value.trim().toLowerCase().startsWith('javascript:')) {
          node.removeAttribute(attr.name);
        }
      }
    });
    return Array.from(template.content.childNodes);
  }

  // Images load asynchronously, so a page measured before its images finish
  // loading reports too small a height. Drop the cached value once the image
  // settles; the next change-detection pass re-measures with real dimensions.
  private watchPendingImages(item: HTMLElement, signature: string) {
    item.querySelectorAll('img').forEach(img => {
      if (img.complete) return;
      const invalidate = () => this.cache.delete(signature);
      img.addEventListener('load', invalidate, { once: true });
      img.addEventListener('error', invalidate, { once: true });
    });
  }
}
